import React, { useEffect, useMemo, useState } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import ErrorIcon from '@mui/icons-material/Error';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import {
  Autocomplete,
  FormControl,
  FormLabel,
  Grid,
  IconButton,
  TextField,
  Typography
} from '@mui/material';
import parse from 'autosuggest-highlight/parse';
import throttle from 'lodash/throttle';

import { notifyBugsnag } from '@/components/bugsnag';

import type { Dispatch, Ref, SetStateAction } from 'react';

interface AddressAnswers {
  address: string;
  city?: string;
  state?: string;
  country?: string;
}

export interface AddressOption {
  description: string;
  place_id?: string;
  terms?: { value: string }[];
  structured_formatting?: {
    main_text: string;
    secondary_text: string;
    main_text_matched_substrings: { offset: number; length: number }[];
  };
}

type PredictionCallback = (results: AddressOption[] | null) => void;
interface PredictionRequest {
  input: string;
  componentRestrictions: { country: string };
  token: object;
  types: string[];
}
interface PredictionService {
  getPlacePredictions: (request: PredictionRequest, callback: PredictionCallback) => void;
}
interface PlaceDetailsService {
  getDetails: (
    request: { placeId: string; fields: string[] },
    callback: (place: { formatted_address?: string } | null, status: string) => void
  ) => void;
}

declare const window: Window & {
  google?: {
    maps: {
      places: {
        AutocompleteService: new () => PredictionService;
        PlacesService: new (element: HTMLDivElement) => PlaceDetailsService;
        AutocompleteSessionToken: new () => object;
        PlacesServiceStatus: { OK: string };
      };
    };
  };
};

interface AddressInputProps<TAnswers extends AddressAnswers> {
  requiredRef: Ref<HTMLDivElement>;
  classes: Record<string, string>;
  value: AddressOption | null;
  setValue: (value: AddressOption | null) => void;
  activeError: boolean;
  answers: TAnswers;
  setAnswers: Dispatch<SetStateAction<TAnswers>>;
  viewJobsAd?: boolean;
}

function loadScript(src: string, position: HTMLElement | null, id: string) {
  if (!position) {
    return;
  }

  const script = document.createElement('script');
  script.setAttribute('async', '');
  script.setAttribute('id', id);
  script.src = src;
  position.appendChild(script);
}

const autocompleteService: { current: PredictionService | null } = {
  current: null
};
const placesService: { current: PlaceDetailsService | null } = { current: null };

const COUNTRY_ABBR: Record<string, string> = {
  Australia: 'au',
  Canada: 'ca',
  'New Zealand': 'nz'
  // Add more countries as necessary
};

const AddressInput = <TAnswers extends AddressAnswers>({
  requiredRef,
  classes,
  value,
  setValue,
  activeError,
  answers,
  setAnswers,
  viewJobsAd
}: AddressInputProps<TAnswers>) => {
  const [inputValue, setInputValue] = React.useState(
    [answers.address, answers.city, answers.state].filter((cur) => cur).join(', ')
  );
  const [options, setOptions] = useState<AddressOption[]>([]);
  const loaded = React.useRef(false);

  if (typeof window !== 'undefined' && !loaded.current) {
    if (!document.querySelector('#google-maps')) {
      loadScript(
        'https://maps.googleapis.com/maps/api/js?key=AIzaSyAF83260uv1i9sjdtsQkK-skJ8fgGCe9_4&libraries=places',
        document.querySelector('head'),
        'google-maps'
      );
    }
    loaded.current = true;
  }

  const fetch = useMemo(
    () =>
      throttle(
        (request: PredictionRequest, callback: PredictionCallback) => {
          autocompleteService.current?.getPlacePredictions(request, callback);
        },
        1000,
        { trailing: true, leading: false }
      ),
    []
  );

  useEffect(() => {
    let active = true;
    const maps = window.google?.maps;
    if (!maps) return undefined;

    autocompleteService.current ??= new maps.places.AutocompleteService();

    if (viewJobsAd && !placesService.current) {
      placesService.current = new maps.places.PlacesService(document.createElement('div'));
    }

    if (!autocompleteService.current) return undefined;

    if (inputValue === '') {
      setOptions(value ? [value] : []);
      return undefined;
    }

    fetch(
      {
        input: inputValue,
        componentRestrictions: {
          country: COUNTRY_ABBR[answers.country ?? ''] || 'au'
        },
        token: new maps.places.AutocompleteSessionToken(),
        types: ['address']
      },
      (results) => {
        if (active) {
          const selectedOptions = value ? [value] : [];
          const newOptions = results ? [...selectedOptions, ...results] : selectedOptions;
          setOptions(newOptions);
        }
      }
    );

    if (value?.terms) {
      const [, state, city, ...address] = value.terms.reverse();
      if (viewJobsAd) {
        if (inputValue !== answers.address) {
          setAnswers((prevAnswers) => ({
            ...prevAnswers,
            address: inputValue
          }));
        }
      } else {
        setAnswers((prevAnswers) => ({
          ...prevAnswers,
          city: city?.value ?? '',
          state: state?.value ?? '',
          address: address
            .reverse()
            .map((cur) => cur.value)
            .join(' ')
        }));
      }
    }

    return () => {
      active = false;
    };
  }, [value, inputValue, fetch, answers.country, answers.address, setAnswers, viewJobsAd]);

  function removeCountryFromAddress(formattedAddress: string) {
    const addressComponents = formattedAddress.split(',');

    if (addressComponents.length < 2) {
      return formattedAddress;
    }

    const addressWithoutCountry = addressComponents
      .slice(0, -1)
      .map((component) => component.trim());

    return addressWithoutCountry.join(', ');
  }

  const handlePlaceSelect = (newValue: AddressOption | null) => {
    if (newValue?.place_id && placesService.current && window.google) {
      const request = {
        placeId: newValue.place_id,
        fields: ['formatted_address', 'place_id']
      };

      const successStatus = window.google.maps.places.PlacesServiceStatus.OK;
      placesService.current.getDetails(request, (place, status) => {
        if (status === String(successStatus) && place?.formatted_address) {
          const formattedAddress = removeCountryFromAddress(place.formatted_address);
          setValue(newValue);
          setInputValue(formattedAddress);
        } else {
          notifyBugsnag(new Error(`Error fetching place details: ${status}`));
        }
      });
    }
  };

  useEffect(() => {
    if (!inputValue && inputValue !== answers.address) {
      setAnswers({
        ...answers,
        city: '',
        state: '',
        address: ''
      });
    }
  }, [inputValue, setAnswers, answers]);

  return (
    <FormControl ref={requiredRef} fullWidth className={`${classes.formControl} ${classes.medium}`}>
      <FormLabel required className={classes.label}>
        {viewJobsAd ? 'Job address' : 'Enter a street address'}
      </FormLabel>
      {!viewJobsAd && (
        <span className={classes.subLabel}>
          We won&apos;t share your street address. We use your location to find candidates in your
          area.
        </span>
      )}
      <Autocomplete
        autoComplete
        filterSelectedOptions
        fullWidth
        includeInputInList
        classes={{ root: classes.autoComplete, popper: classes.autoCompletePopper }}
        filterOptions={(x) => x}
        forcePopupIcon={false}
        getOptionLabel={(option) => (typeof option === 'string' ? option : option.description)}
        options={options}
        value={value}
        onChange={(event, newValue) => {
          setOptions(newValue ? [newValue, ...options] : options);
          if (viewJobsAd) handlePlaceSelect(newValue);
          else setValue(newValue);
        }}
        onInputChange={(event, newInputValue) => {
          setInputValue(newInputValue);
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            fullWidth
            error={activeError}
            placeholder="Select an address"
            variant="outlined"
            slotProps={{
              ...params.slotProps,

              input: {
                ...params.slotProps.input,
                classes: {
                  root: classes.autoCompleteRoot
                },
                endAdornment: (
                  <>
                    {activeError ? (
                      <div className={classes.error}>
                        <ErrorIcon color="error" />
                        <span>This field is required</span>
                      </div>
                    ) : null}
                    {inputValue ? (
                      <IconButton
                        className={classes.autoCompleteClose}
                        onClick={() => {
                          setAnswers({ ...answers, address: '', city: '', state: '' });
                          setValue(null);
                          setInputValue('');
                        }}
                      >
                        <CloseIcon />
                      </IconButton>
                    ) : null}
                  </>
                )
              }
            }}
          />
        )}
        renderOption={(props, option) => {
          if (!option.structured_formatting) return null;
          const matches = option.structured_formatting.main_text_matched_substrings;
          const parts: { text: string; highlight: boolean }[] = parse(
            option.structured_formatting.main_text,
            matches.map((match) => [match.offset, match.offset + match.length])
          );

          return (
            <li {...props}>
              <Grid
                container
                sx={{
                  alignItems: 'center'
                }}
              >
                <Grid>
                  <LocationOnIcon className={classes.icon} />
                </Grid>
                <Grid size="grow">
                  {parts.map((part) => (
                    <span key={part.text} style={{ fontWeight: part.highlight ? 700 : 400 }}>
                      {part.text}
                    </span>
                  ))}
                  <Typography color="textSecondary" variant="body2">
                    {option.structured_formatting.secondary_text}
                  </Typography>
                </Grid>
              </Grid>
            </li>
          );
        }}
      />
    </FormControl>
  );
};

export default AddressInput;
